import React, { useState } from 'react';
import { Play, X, Shuffle, User, Video, Loader2, ListVideo, ArrowUpDown, Calendar, Clock, SortAsc } from 'lucide-react';

const YouTubeMusicVideosSection = ({
  loading,
  playlists = [],
  activePlaylist,
  onPlaylistChange,
  featuredVideo,
  artists,
  videos,
  filteredVideos,
  selectedArtist,
  onArtistSelect,
  onPlayVideo,
  onShufflePlay,
  sortOption = 'default',
  onSortChange
}) => {
  const [showSortMenu, setShowSortMenu] = useState(false);
  
  const sortOptions = [
    { id: 'default', label: 'Default', icon: ListVideo },
    { id: 'title-asc', label: 'Title (A-Z)', icon: SortAsc },
    { id: 'title-desc', label: 'Title (Z-A)', icon: SortAsc },
    { id: 'artist-asc', label: 'Artist (A-Z)', icon: User },
    { id: 'artist-desc', label: 'Artist (Z-A)', icon: User },
    { id: 'published-newest', label: 'Published (Newest)', icon: Calendar },
    { id: 'published-oldest', label: 'Published (Oldest)', icon: Calendar },
    { id: 'added-newest', label: 'Added (Newest)', icon: Clock },
    { id: 'added-oldest', label: 'Added (Oldest)', icon: Clock },
  ];
  
  const currentSort = sortOptions.find(s => s.id === sortOption) || sortOptions[0];
  
  return (
    <div className="space-y-6">
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Loader2 size={48} className="animate-spin text-cyan-400 mx-auto mb-4" />
            <p className="text-gray-400">Loading music videos...</p>
          </div>
        </div>
      )}

      {!loading && (
        <>
          {/* Featured Video Hero Section */}
          {featuredVideo && (
            <div className="relative rounded-2xl overflow-hidden bg-black border border-white/10 hover:border-white/30 hover:shadow-[0_0_30px_rgba(255,255,255,0.1)] transition-all duration-300">
              <div className="h-48 md:h-56 lg:h-64 relative">
                <img 
                  src={featuredVideo.thumbnail}
                  alt={featuredVideo.title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
                <div className="absolute inset-0 flex items-center px-6 md:px-10">
                  <div>
                    <p className="text-white text-xs font-medium mb-1">FEATURED VIDEO</p>
                    <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-white mb-1">
                      {featuredVideo.songTitle || featuredVideo.title}
                    </h1>
                    <p className="text-gray-300 text-sm md:text-base mb-3">{featuredVideo.artist}</p>
                    <button
                      onClick={() => onPlayVideo(featuredVideo)}
                      className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-lg font-semibold hover:bg-gray-200 transition-colors text-sm"
                      data-testid="featured-video-watch-btn"
                    >
                      <Play size={16} fill="black" />
                      Watch Now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Artists Section */}
          {artists.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-white">Artists ({artists.length})</h2>
                {selectedArtist && (
                  <button
                    onClick={() => onArtistSelect(null)}
                    className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                    data-testid="clear-artist-filter"
                  >
                    <X size={16} />
                    Clear Filter
                  </button>
                )}
              </div>
              <div className="flex gap-3 overflow-x-auto pb-2 custom-scrollbar">
                {artists.map((artist, idx) => (
                  <button
                    key={idx}
                    onClick={() => onArtistSelect(
                      selectedArtist === artist.name ? null : artist.name
                    )}
                    className={`flex-shrink-0 flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-300 ${
                      selectedArtist === artist.name
                        ? 'bg-cyan-500/20 border-cyan-500 shadow-[0_0_20px_rgba(6,182,212,0.3)]'
                        : 'bg-black border-white/10 hover:border-white/30 hover:shadow-[0_0_15px_rgba(255,255,255,0.1)]'
                    }`}
                    data-testid={`artist-${artist.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="w-16 h-16 rounded-full bg-gray-800 overflow-hidden border border-white/20">
                      {artist.thumbnail ? (
                        <img 
                          src={artist.thumbnail}
                          alt={artist.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User size={24} className="text-gray-500" />
                        </div>
                      )}
                    </div>
                    <span className="text-white text-xs font-medium text-center max-w-[80px] truncate">
                      {artist.name}
                    </span>
                    <span className="text-gray-500 text-xs">{artist.videos?.length || 0} videos</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Playlist Tabs - Positioned between Artists and All Music Videos */}
          {playlists.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {playlists.map((playlist) => (
                <button
                  key={playlist.id}
                  onClick={() => onPlaylistChange(playlist.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap text-sm font-medium transition-all duration-300 border ${
                    activePlaylist === playlist.id
                      ? 'bg-white text-black border-white'
                      : 'bg-black text-white border-white/30 hover:border-white hover:shadow-[0_0_15px_rgba(255,255,255,0.2)]'
                  }`}
                  data-testid={`playlist-tab-${playlist.id}`}
                >
                  <span className="flex items-center gap-2">
                    <ListVideo size={14} />
                    {playlist.name}
                  </span>
                </button>
              ))}
            </div>
          )}

          {/* Music Videos Grid */}
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <h2 className="text-xl font-bold text-white">
                  {selectedArtist ? `${selectedArtist} Videos` : 'All Music Videos'}
                </h2>
                {/* Shuffle Button */}
                <button
                  onClick={onShufflePlay}
                  className="flex items-center gap-2 px-3 py-1.5 bg-black border border-white/20 rounded-lg hover:border-cyan-500/50 hover:bg-cyan-500/10 hover:shadow-[0_0_15px_rgba(6,182,212,0.2)] transition-all group"
                  title="Shuffle play all videos"
                  data-testid="shuffle-music-videos"
                >
                  <Shuffle size={16} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                  <span className="text-sm text-gray-400 group-hover:text-cyan-400 transition-colors">Shuffle</span>
                </button>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Sort Dropdown */}
                <div className="relative">
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-black border border-white/20 rounded-lg hover:border-cyan-500/50 hover:bg-cyan-500/10 hover:shadow-[0_0_15px_rgba(6,182,212,0.2)] transition-all group"
                    data-testid="sort-dropdown-btn"
                  >
                    <ArrowUpDown size={16} className="text-gray-400 group-hover:text-cyan-400 transition-colors" />
                    <span className="text-sm text-gray-400 group-hover:text-cyan-400 transition-colors">{currentSort.label}</span>
                  </button>
                  
                  {showSortMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-gray-900 border border-white/20 rounded-lg shadow-xl z-50 overflow-hidden">
                      {sortOptions.map((option) => {
                        const IconComponent = option.icon;
                        return (
                          <button
                            key={option.id}
                            onClick={() => {
                              onSortChange(option.id);
                              setShowSortMenu(false);
                            }}
                            className={`w-full flex items-center gap-2 px-4 py-2.5 text-left text-sm transition-colors ${
                              sortOption === option.id
                                ? 'bg-cyan-500/20 text-cyan-400'
                                : 'text-gray-300 hover:bg-white/10 hover:text-white'
                            }`}
                            data-testid={`sort-option-${option.id}`}
                          >
                            <IconComponent size={14} />
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                <span className="text-gray-400 text-sm">{filteredVideos.length} videos</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {filteredVideos.map((video, idx) => (
                <button
                  key={video.id}
                  onClick={() => onPlayVideo(video, idx)}
                  className="group relative bg-black border border-white/10 rounded-xl overflow-hidden text-left hover:border-white/30 hover:shadow-[0_0_20px_rgba(255,255,255,0.1)] transition-all duration-300"
                  data-testid={`music-video-${idx}`}
                >
                  {/* Thumbnail */}
                  <div className="aspect-video relative overflow-hidden">
                    <img 
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play size={24} className="text-black ml-1" fill="black" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Info */}
                  <div className="p-3">
                    <h3 className="text-white text-sm font-medium truncate mb-1">
                      {video.songTitle || video.title}
                    </h3>
                    <p className="text-gray-400 text-xs truncate">{video.artist}</p>
                  </div>
                </button>
              ))}
            </div>

            {/* Empty State */}
            {filteredVideos.length === 0 && !loading && (
              <div className="text-center py-12 bg-black border border-white/10 rounded-xl">
                <Video size={48} className="text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-300 mb-2">No music videos found</h3>
                <p className="text-gray-500 text-sm">
                  {selectedArtist 
                    ? `No videos found for ${selectedArtist}`
                    : 'Connect your YouTube playlist to see music videos'}
                </p>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default YouTubeMusicVideosSection;
